import { usagePeriodKey } from '@alicerce/contracts';
import { Alert, Button, Card, CardBody, CardHeader, cn, Dialog, Field, formatBRL, formatDateTime, formatNumber, MoneyInput, PageHeader, PageLoader, Select, Stat, Table, Td, Th, Tr } from '@alicerce/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { LoadMore } from '@/components/load-more';
import { PaymentInstructions, type ChargeView } from '@/components/payment-instructions';
import { errorMessage } from '@/lib/api';
import { useCursorList } from '@/lib/hooks';
import { useApi, useCurrent } from '@/lib/session';
import { CREDIT_TX, type CreditTransaction, type UsageLine } from './types';

/** Os últimos `n` meses no formato AAAA-MM, do mais recente para o mais antigo. */
export function lastPeriods(n: number, now = new Date()) {
  const out: string[] = [];
  const d = new Date(now);
  d.setUTCDate(15);
  for (let i = 0; i < n; i++) {
    out.push(usagePeriodKey(d));
    d.setUTCMonth(d.getUTCMonth() - 1);
  }
  return out;
}

const monthLabel = (period: string) => {
  const [y, m] = period.split('-').map(Number);
  const s = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y!, m! - 1, 15)));
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export function UsagePage() {
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const periods = lastPeriods(6);
  const period = periods.includes(params.get('periodo') ?? '') ? params.get('periodo')! : periods[0]!;
  const usage = useQuery({
    queryKey: ['billing', 'usage', period],
    queryFn: () => api.get<{ periodKey: string; metrics: UsageLine[] }>('/v1/billing/usage', { query: { period } }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Consumo e créditos"
        description={
          <>
            O que você usou além do plano. <Link to="/assinatura" className="text-brand-700 hover:underline">Voltar para a assinatura</Link>
          </>
        }
      />
      <Card>
        <CardHeader
          title="Consumo do mês"
          actions={
            <Select aria-label="Mês" className="w-48" value={period} onChange={(e) => setParams({ periodo: e.target.value })}>
              {periods.map((p) => (
                <option key={p} value={p}>
                  {monthLabel(p)}
                </option>
              ))}
            </Select>
          }
        />
        <CardBody>
          {usage.isPending ? (
            <PageLoader />
          ) : usage.isError ? (
            <Alert tone="danger">{errorMessage(usage.error)}</Alert>
          ) : usage.data.metrics.length === 0 ? (
            <p className="text-sm text-muted">Nada medido neste mês. Notas fiscais e conversas de WhatsApp aparecem aqui quando os módulos estão ativos.</p>
          ) : (
            <ul className="space-y-5">
              {usage.data.metrics.map((m) => (
                <li key={m.metric} className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{m.label}</span>
                    <span className="tabular-nums">
                      {formatNumber(m.used)}
                      {m.included > 0 && ` de ${formatNumber(m.included)} incluídas`}
                    </span>
                  </div>
                  {m.included > 0 && (
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-label={m.label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(100, m.percent)}>
                      <div className={cn('h-full', m.percent >= 100 ? 'bg-danger' : m.percent >= 80 ? 'bg-warning' : 'bg-brand-600')} style={{ width: `${Math.min(100, m.percent)}%` }} />
                    </div>
                  )}
                  {m.overageUnits > 0 && (
                    <p className="text-sm text-danger">
                      {formatNumber(m.overageUnits)} além do plano × {formatBRL(m.overageUnitCents)} = {formatBRL(m.overageCents)}, cobrado na próxima fatura.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      <CreditsCard />
    </div>
  );
}

function CreditsCard() {
  const api = useApi();
  const { can, isModuleActive } = useCurrent();
  const [buying, setBuying] = useState(false);
  const balance = useQuery({ queryKey: ['billing', 'credits'], queryFn: () => api.get<{ balanceCents: number }>('/v1/billing/credits') });
  const tx = useCursorList<CreditTransaction>(['billing', 'credit-transactions'], '/v1/billing/credits/transactions', { limit: 20 });
  if (!isModuleActive('ads') && !balance.data?.balanceCents) return null;
  return (
    <Card>
      <CardHeader
        title="Créditos de mídia"
        description="Saldo usado para pagar os anúncios que a Gestora publica para você."
        actions={can('billing:manage') && <Button onClick={() => setBuying(true)}>Comprar créditos</Button>}
      />
      <CardBody>{balance.data && <Stat label="Saldo" value={formatBRL(balance.data.balanceCents)} />}</CardBody>
      {tx.items.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Quando</Th>
                <Th>Movimento</Th>
                <Th className="text-right">Valor</Th>
                <Th className="text-right">Saldo</Th>
              </tr>
            </thead>
            <tbody>
              {tx.items.map((t) => (
                <Tr key={t.id}>
                  <Td>{formatDateTime(t.createdAt)}</Td>
                  <Td>{CREDIT_TX[t.type] ?? t.type}</Td>
                  <Td className={cn('text-right tabular-nums', t.amountCents < 0 ? 'text-danger' : 'text-success')}>{formatBRL(t.amountCents)}</Td>
                  <Td className="text-right tabular-nums">{formatBRL(t.balanceAfterCents)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          <LoadMore {...tx} />
        </>
      )}
      {buying && <BuyCreditsDialog onClose={() => setBuying(false)} />}
    </Card>
  );
}

function BuyCreditsDialog({ onClose }: { onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState<number | null>(10000);
  const buy = useMutation({
    mutationFn: () =>
      api.post<ChargeView & { feeCents: number; creditsCents: number }>('/v1/billing/credits/purchase', { amountCents: amount, method: 'PIX' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing'] }),
  });
  return (
    <Dialog open onClose={onClose} title="Comprar créditos">
      {buy.data ? (
        <div className="space-y-4">
          <PaymentInstructions
            charge={{ ...buy.data, method: 'PIX', dueDate: buy.data.dueDate ?? new Date().toISOString() }}
            title="Pague o Pix"
            description={`Entram ${formatBRL(buy.data.creditsCents)} de saldo${buy.data.feeCents ? ` (taxa de gestão de ${formatBRL(buy.data.feeCents)})` : ''}.`}
          />
          <div className="flex justify-end">
            <Button onClick={onClose}>Fechar</Button>
          </div>
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            buy.mutate();
          }}
        >
          <Field label="Valor">{(a11y) => <MoneyInput {...a11y} value={amount} onChange={setAmount} />}</Field>
          {buy.isError && <Alert tone="danger">{errorMessage(buy.error)}</Alert>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Voltar
            </Button>
            <Button type="submit" loading={buy.isPending} disabled={!amount}>
              Gerar Pix
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
